import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { customerApi, scheduleApi, paymentApi } from '../api';
import {
  CONTRACT_STATUSES, CONTRACT_STATUS_COLORS,
  SCHEDULE_CATEGORIES, SCHEDULE_CATEGORY_COLORS, SCHEDULE_STATUS_COLORS,
  PAYMENT_TYPE_COLORS, PAYMENT_STATUS_COLORS, PAYMENT_TYPES, PAYMENT_STATUSES, PAYMENT_METHODS,
  ITEM_CATEGORIES,
  CUSTOMER_SOURCES,
  EVENT_TYPES, EVENT_TYPE_COLORS,
  CONSULTATION_TYPES, CONSULTATION_TYPE_COLORS,
} from '../constants';

type TabType = 'OVERVIEW' | 'ITEMS' | 'PAYMENTS' | 'MEMOS';

const TABS: { id: TabType; label: string; icon: string }[] = [
  { id: 'OVERVIEW', label: '기본정보', icon: 'ri-user-line' },
  { id: 'ITEMS', label: '추가매출', icon: 'ri-list-check' },
  { id: 'PAYMENTS', label: '결제정보', icon: 'ri-money-dollar-circle-line' },
  { id: 'MEMOS', label: '상담메모', icon: 'ri-chat-3-line' },
];

export default function WeddingHallCustomerDetail() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = Number(searchParams.get('id'));
  const initialTab = (searchParams.get('tab') as TabType) || 'OVERVIEW';

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const changeTab = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ id: String(customerId), tab });
  };
  const [customer, setCustomer] = useState<any>(null);
  const [tabData, setTabData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});

  // 메모 모달
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [editingMemo, setEditingMemo] = useState<any>(null);
  const [memoForm, setMemoForm] = useState<any>({});

  // 일정 모달
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleItems, setScheduleItems] = useState<any[]>([]);

  // 추가매출 품목 모달
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm] = useState<any>({});
  const [editingItem, setEditingItem] = useState<any>(null);

  // 결제 등록 모달
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState<any>({});
  const [selectedPayment, setSelectedPayment] = useState<any>(null);

  const fetchData = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const data = await customerApi.detail(customerId, activeTab);
      setCustomer(data.customer);
      setTabData(data);
      if (activeTab === 'OVERVIEW') {
        setEditForm(data.customer || {});
      }
    } catch (err) {
      console.error('고객 상세 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdate = async () => {
    try {
      const { whCustomerIdx, ownerVendor, deleteYn, createdDt, updatedDt, ...customerData } = editForm;
      await customerApi.update(customerId, customerData);
      alert('수정되었습니다.');
      setIsEditing(false);
      fetchData();
    } catch (err: any) {
      alert(`수정 실패: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!confirm('정말 이 고객을 삭제하시겠습니까?')) return;
    try {
      await customerApi.delete(customerId);
      alert('삭제되었습니다.');
      navigate('/wedding-hall/customers');
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // --- 메모 핸들러 ---
  const openMemoModal = (memo?: any) => {
    if (memo) {
      setEditingMemo(memo);
      setMemoForm({ ...memo });
    } else {
      setEditingMemo(null);
      setMemoForm({ consultationDt: new Date().toISOString().slice(0, 16) });
    }
    setShowMemoModal(true);
  };

  const handleSaveMemo = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingMemo) {
        await customerApi.memoUpdate(editingMemo.whConsultationMemoIdx, memoForm);
        alert('메모가 수정되었습니다.');
      } else {
        await customerApi.memoCreate(customerId, memoForm);
        alert('메모가 등록되었습니다.');
      }
      setShowMemoModal(false);
      fetchData();
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  const handleDeleteMemo = async (id: number) => {
    if (!confirm('이 메모를 삭제하시겠습니까?')) return;
    try {
      await customerApi.memoDelete(id);
      alert('삭제되었습니다.');
      fetchData();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // --- 일정 핸들러 ---
  const emptyScheduleItem = () => ({
    scheduleCategory: 'CONSULTATION',
    title: '',
    startDt: '',
    endDt: '',
    memo: '',
  });

  const openScheduleModal = () => {
    setScheduleItems([emptyScheduleItem()]);
    setShowScheduleModal(true);
  };

  const handleScheduleItemChange = (index: number, field: string, value: string) => {
    setScheduleItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addScheduleItem = () => {
    setScheduleItems(prev => [...prev, emptyScheduleItem()]);
  };

  const removeScheduleItem = (index: number) => {
    if (scheduleItems.length === 1) return;
    setScheduleItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = scheduleItems.filter(item => item.title && item.startDt);
    if (validItems.length === 0) {
      alert('제목과 시작일시는 필수입니다.');
      return;
    }
    try {
      const customerName = customer?.groomName
        ? `${customer.groomName}/${customer.brideName}`
        : customer?.contactName || '';
      await Promise.all(validItems.map(item =>
        scheduleApi.create({
          section: 'HALL',
          ...item,
          endDt: item.endDt || item.startDt,
          whCustomerIdx: customerId,
          customerName,
          customerPhone: customer?.groomPhone || customer?.contactPhone || '',
        })
      ));
      alert(`${validItems.length}건의 일정이 등록되었습니다.`);
      setShowScheduleModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // --- 추가매출 품목 핸들러 ---
  const openItemModal = (item?: any) => {
    if (item) {
      setEditingItem(item);
      setItemForm({ ...item });
    } else {
      setEditingItem(null);
      setItemForm({
        itemCategory: 'MEAL',
        productName: '',
        quantity: 1,
        unitPrice: 0,
        memo: '',
      });
    }
    setShowItemModal(true);
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await customerApi.updateItem(editingItem.whContractItemIdx, itemForm);
        alert('품목이 수정되었습니다.');
      } else {
        await customerApi.addItem(customerId, itemForm);
        alert('품목이 등록되었습니다.');
      }
      setShowItemModal(false);
      fetchData();
    } catch (err: any) {
      alert(`저장 실패: ${err.message}`);
    }
  };

  const handleDeleteItem = async (id: number) => {
    if (!confirm('이 품목을 삭제하시겠습니까?')) return;
    try {
      await customerApi.deleteItem(id);
      alert('삭제되었습니다.');
      fetchData();
    } catch (err: any) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  // --- 결제 핸들러 ---
  const openPaymentModal = () => {
    setPaymentForm({
      whCustomerIdx: customerId,
      items: [{ paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'CONTRACT', status: 'COMPLETED', paymentMethod: 'CARD', memo: '' }],
    });
    setShowPaymentModal(true);
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    const items = paymentForm.items || [paymentForm];
    const validItems = items.filter((i: any) => i.amount > 0 && i.paidDt);
    if (validItems.length === 0) {
      alert('결제 금액과 결제일을 입력해주세요.');
      return;
    }
    try {
      await Promise.all(validItems.map((item: any) =>
        paymentApi.create({
          whCustomerIdx: customerId,
          ...item,
          amount: Number(item.amount),
        })
      ));
      // 예정 상태인 결제는 일정에 자동 등록
      const scheduledItems = validItems.filter((i: any) => i.status === 'SCHEDULED');
      if (scheduledItems.length > 0 && customer) {
        const custName = customer.groomName
          ? `${customer.groomName}/${customer.brideName}`
          : customer.contactName || '';
        const getTypeLabel = (t: string) => ({ DEPOSIT: '예약금', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '결제' }[t] || '결제');
        await Promise.all(scheduledItems.map((i: any) =>
          scheduleApi.create({
            section: 'HALL',
            scheduleCategory: 'PAYMENT',
            title: `${custName} ${getTypeLabel(i.paymentType)} ${Number(i.amount).toLocaleString()}원`,
            startDt: i.paidDt,
            whCustomerIdx: customerId,
            customerName: custName,
          }).catch(() => {})
        ));
      }

      alert(`${validItems.length}건의 결제가 등록되었습니다.`);
      setShowPaymentModal(false);
      fetchData();
    } catch (err: any) {
      alert(`등록 실패: ${err.message}`);
    }
  };

  // 고객 표시명
  const getCustomerDisplayName = (c: any) => {
    if (c?.groomName && c?.brideName) return `${c.groomName} / ${c.brideName}`;
    if (c?.groomName || c?.brideName) return c.groomName || c.brideName;
    return c?.contactName || '-';
  };

  const formatDate = (dt: string) => {
    if (!dt) return '-';
    try { return new Date(dt).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return dt; }
  };

  const formatShortDate = (dt: string) => {
    if (!dt) return '-';
    try { return new Date(dt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }); }
    catch { return dt; }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/wedding-hall/customers')}
              className="text-gray-600 hover:text-gray-800 w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded transition-colors cursor-pointer">
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">고객 상세정보</h1>
              <p className="text-sm text-gray-600 mt-1">웨딩홀 고객 상세 정보</p>
            </div>
          </div>
          {activeTab === 'OVERVIEW' && (
            <div className="flex items-center gap-3">
              {customer && (
                <select value={customer.status || ''} onChange={async (e) => {
                  try {
                    await customerApi.update(customerId, { status: e.target.value });
                    fetchData();
                  } catch (err: any) { alert(`상태 변경 실패: ${err.message}`); }
                }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
                  {CONTRACT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {isEditing ? (
                <>
                  <button onClick={() => setIsEditing(false)}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium cursor-pointer">취소</button>
                  <button onClick={handleUpdate}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">저장</button>
                </>
              ) : (
                <>
                  <button onClick={() => setIsEditing(true)}
                    className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 flex items-center gap-2 cursor-pointer">
                    <i className="ri-edit-line"></i> 수정
                  </button>
                  <button onClick={handleDelete}
                    className="bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 flex items-center gap-2 cursor-pointer">
                    <i className="ri-delete-bin-line"></i> 삭제
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* 고객 요약 카드 */}
        {customer && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center">
                <i className="ri-building-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-xl font-bold text-gray-800">
                    {getCustomerDisplayName(customer)}
                  </h2>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${CONTRACT_STATUS_COLORS[customer.status] || 'bg-gray-100 text-gray-700'}`}>
                    {customer.status}
                  </span>
                  {customer.eventType && (
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${EVENT_TYPE_COLORS[customer.eventType] || 'bg-gray-100 text-gray-700'}`}>
                      {customer.eventType}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  {customer.groomPhone && <span><i className="ri-phone-line mr-1"></i>신랑: {customer.groomPhone}</span>}
                  {customer.bridePhone && <span><i className="ri-phone-line mr-1"></i>신부: {customer.bridePhone}</span>}
                  {customer.contactPhone && !customer.groomPhone && <span><i className="ri-phone-line mr-1"></i>담당자: {customer.contactPhone}</span>}
                  {customer.eventDt && <span><i className="ri-calendar-line mr-1"></i>행사일: {formatDate(customer.eventDt)}</span>}
                  {customer.eventTime && <span><i className="ri-time-line mr-1"></i>{customer.eventTime}</span>}
                  {customer.venueName && <span><i className="ri-building-line mr-1"></i>예식홀: {customer.venueName}</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 탭 메뉴 */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => { changeTab(tab.id); setTabData(null); }}
                className={`flex-1 px-4 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>{tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 탭 컨텐츠 */}
        {isLoading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : (
          <>
            {/* 기본정보 탭 */}
            {activeTab === 'OVERVIEW' && customer && (
              <div className="space-y-6">
                {/* 기본 정보 */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-6">기본 정보</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {/* 웨딩 고객인 경우: 신랑/신부 */}
                    {[
                      { label: '신랑명', key: 'groomName' },
                      { label: '신랑 연락처', key: 'groomPhone' },
                      { label: '신부명', key: 'brideName' },
                      { label: '신부 연락처', key: 'bridePhone' },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                        {isEditing ? (
                          <input type="text" value={editForm[field.key] || ''}
                            onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer[field.key] || '-'}</p>
                        )}
                      </div>
                    ))}
                    {/* 비웨딩 행사 담당자 */}
                    {[
                      { label: '담당자명', key: 'contactName' },
                      { label: '담당자 연락처', key: 'contactPhone' },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                        {isEditing ? (
                          <input type="text" value={editForm[field.key] || ''}
                            onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer[field.key] || '-'}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* 행사/계약 정보 */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-bold text-gray-700 mb-4">행사 및 계약 정보</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">행사유형</label>
                        {isEditing ? (
                          <select value={editForm.eventType || ''} onChange={(e) => setEditForm({ ...editForm, eventType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">선택</option>
                            {EVENT_TYPES.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
                          </select>
                        ) : (
                          <p className="text-gray-900">{customer.eventType || '-'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">계약구분</label>
                        {isEditing ? (
                          <select value={editForm.status || ''} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            {CONTRACT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <p className="text-gray-900">{customer.status || '-'}</p>
                        )}
                      </div>
                      {[
                        { label: '홀드일', key: 'holdDt', type: 'date' },
                        { label: '계약일', key: 'contractDt', type: 'date' },
                        { label: '행사일', key: 'eventDt', type: 'date' },
                      ].map(field => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-500 mb-1">{field.label}</label>
                          {isEditing ? (
                            <input type={field.type} value={editForm[field.key] ? editForm[field.key].slice(0, 10) : ''}
                              onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          ) : (
                            <p className="text-gray-900">{formatDate(customer[field.key])}</p>
                          )}
                        </div>
                      ))}
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">행사시간</label>
                        {isEditing ? (
                          <input type="time" value={editForm.eventTime || ''}
                            onChange={(e) => setEditForm({ ...editForm, eventTime: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer.eventTime || '-'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">예식홀</label>
                        {isEditing ? (
                          <input type="text" value={editForm.venueName || ''}
                            onChange={(e) => setEditForm({ ...editForm, venueName: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer.venueName || '-'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">식대메뉴</label>
                        {isEditing ? (
                          <input type="text" value={editForm.mealMenu || ''}
                            onChange={(e) => setEditForm({ ...editForm, mealMenu: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer.mealMenu || '-'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">식대단가</label>
                        {isEditing ? (
                          <input type="number" value={editForm.mealUnitPrice || ''}
                            onChange={(e) => setEditForm({ ...editForm, mealUnitPrice: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer.mealUnitPrice ? `${Number(customer.mealUnitPrice).toLocaleString()}원` : '-'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">보증인원</label>
                        {isEditing ? (
                          <input type="number" value={editForm.guaranteedGuests || ''}
                            onChange={(e) => setEditForm({ ...editForm, guaranteedGuests: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer.guaranteedGuests ? `${customer.guaranteedGuests}명` : '-'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">대관료</label>
                        {isEditing ? (
                          <input type="number" value={editForm.venueRentalFee || ''}
                            onChange={(e) => setEditForm({ ...editForm, venueRentalFee: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        ) : (
                          <p className="text-gray-900">{customer.venueRentalFee ? `${Number(customer.venueRentalFee).toLocaleString()}원` : '-'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">고객경로</label>
                        {isEditing ? (
                          <select value={editForm.customerSource || ''} onChange={(e) => setEditForm({ ...editForm, customerSource: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">선택</option>
                            {CUSTOMER_SOURCES.map(cs => <option key={cs.value} value={cs.value}>{cs.label}</option>)}
                          </select>
                        ) : (
                          <p className="text-gray-900">{customer.customerSource || '-'}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {(customer.memo || isEditing) && (
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-500 mb-1">메모</label>
                      {isEditing ? (
                        <textarea value={editForm.memo || ''} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })} rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                      ) : (
                        <p className="text-gray-900 whitespace-pre-wrap">{customer.memo || '-'}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 추가매출 탭 */}
            {activeTab === 'ITEMS' && (
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">추가매출 품목</h3>
                  <button onClick={() => openItemModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                    <i className="ri-add-line"></i> 품목 추가
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">카테고리</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상품명</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">수량</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">단가</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">소계</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">비고</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">관리</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(tabData?.items || []).length === 0 ? (
                        <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">추가매출 품목이 없습니다.</td></tr>
                      ) : (tabData?.items || []).map((item: any) => {
                        const categoryLabel = ITEM_CATEGORIES.find(ic => ic.value === item.itemCategory)?.label || item.itemCategory;
                        const subtotal = (item.unitPrice || 0) * (item.quantity || 1);
                        return (
                          <tr key={item.whContractItemIdx} className="hover:bg-blue-50/30">
                            <td className="px-4 py-3">
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-600">{categoryLabel}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.productName || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right">{item.quantity || 1}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right">{Number(item.unitPrice || 0).toLocaleString()}원</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{subtotal.toLocaleString()}원</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{item.memo || '-'}</td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => openItemModal(item)} className="text-blue-600 hover:text-blue-800 text-sm mr-2 cursor-pointer">수정</button>
                              <button onClick={() => handleDeleteItem(item.whContractItemIdx)} className="text-red-600 hover:text-red-800 text-sm cursor-pointer">삭제</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* 합계 */}
                  {(tabData?.items || []).length > 0 && (
                    <div className="p-4 bg-blue-50 border-t border-blue-200">
                      <div className="flex justify-end text-sm">
                        <span className="font-semibold text-gray-800 mr-4">추가매출 합계:</span>
                        <span className="font-bold text-blue-700">
                          {(tabData?.items || []).reduce((sum: number, item: any) => sum + ((item.unitPrice || 0) * (item.quantity || 1)), 0).toLocaleString()}원
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 결제정보 탭 */}
            {activeTab === 'PAYMENTS' && (() => {
              const payments = (tabData?.payments || []);
              const totalPaid = payments.reduce((sum: number, p: any) => sum + Number(p.amount || p.paid_amount || 0), 0);
              const getPaymentType = (t: string) => ({ DEPOSIT: '예약금', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '기타' }[t] || t || '-');
              const getPaymentMethod = (m: string) => ({ CARD: '카드', TRANSFER: '계좌이체', CASH: '현금' }[m] || m || '-');
              const getPaymentStatus = (s: string) => ({ COMPLETED: '완료', SCHEDULED: '예정', REFUNDED: '환불', CANCELLED: '취소' }[s] || s || '-');

              return (
                <div className="space-y-4">
                  {/* 결제 요약 */}
                  <div className="bg-white rounded-lg shadow-sm p-5">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">결제 완료 총액</div>
                        <div className="text-lg font-bold text-blue-600">{totalPaid.toLocaleString()}원</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">결제 건수</div>
                        <div className="text-lg font-bold text-gray-900">{payments.length}건</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="text-lg font-bold text-gray-800">결제 내역</h3>
                      <button onClick={openPaymentModal}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                        <i className="ri-add-line"></i> 결제 등록
                      </button>
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제일</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제유형</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">결제수단</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">메모</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {payments.length === 0 ? (
                          <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">결제 내역이 없습니다.</td></tr>
                        ) : payments.map((p: any) => {
                          const paymentType = p.paymentType || p.payment_type || '';
                          const paymentMethod = p.paymentMethod || p.payment_method || '';
                          const paidDt = p.paidDt || p.paid_dt || '';
                          const status = p.status || '';
                          return (
                            <tr key={p.whPaymentIdx || p.wh_payment_idx} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedPayment(p)}>
                              <td className="px-4 py-4 text-sm text-gray-700">{formatShortDate(paidDt)}</td>
                              <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_TYPE_COLORS[paymentType] || 'bg-gray-100 text-gray-700'}`}>{getPaymentType(paymentType)}</span></td>
                              <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{Number(p.amount || 0).toLocaleString()}원</td>
                              <td className="px-4 py-4 text-sm text-gray-700">{getPaymentMethod(paymentMethod)}</td>
                              <td className="px-4 py-4"><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{getPaymentStatus(status)}</span></td>
                              <td className="px-4 py-4 text-sm text-gray-500">{p.memo || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* 상담메모 탭 */}
            {activeTab === 'MEMOS' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800">상담 메모</h3>
                  <button onClick={() => openMemoModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2 cursor-pointer">
                    <i className="ri-add-line"></i> 메모 등록
                  </button>
                </div>
                {(tabData?.memos || []).length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500">상담 메모가 없습니다.</div>
                ) : (tabData?.memos || []).map((memo: any) => (
                  <div key={memo.whConsultationMemoIdx} className="bg-white rounded-lg shadow-sm p-6 mb-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-gray-500">{memo.consultationDt}</div>
                          {memo.consultationType && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${CONSULTATION_TYPE_COLORS[memo.consultationType] || 'bg-gray-100 text-gray-700'}`}>
                              {CONSULTATION_TYPES.find(ct => ct.value === memo.consultationType)?.label || memo.consultationType}
                            </span>
                          )}
                        </div>
                        {memo.createdBy && <div className="text-xs text-gray-400">작성자: {memo.createdBy.name}</div>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openMemoModal(memo)} className="text-blue-600 hover:text-blue-800 text-sm cursor-pointer">수정</button>
                        <button onClick={() => handleDeleteMemo(memo.whConsultationMemoIdx)} className="text-red-600 hover:text-red-800 text-sm cursor-pointer">삭제</button>
                      </div>
                    </div>
                    {memo.budgetRange && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-green-500">예산:</span> {memo.budgetRange}</p>
                    )}
                    {memo.expectedGuests && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-blue-500">예상 하객수:</span> {memo.expectedGuests}</p>
                    )}
                    {memo.preferredMenu && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-orange-500">선호 메뉴:</span> {memo.preferredMenu}</p>
                    )}
                    {memo.specialRequests && (
                      <p className="text-sm mb-2"><span className="text-xs font-medium text-purple-500">특별 요청:</span> {memo.specialRequests}</p>
                    )}
                    {memo.content && <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg mt-3 whitespace-pre-wrap">{memo.content}</p>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* 일정 등록 모달 */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-lg font-bold">일정 등록</h2>
                  <p className="text-xs text-gray-400 mt-0.5">여러 일정을 한번에 등록할 수 있습니다</p>
                </div>
                <button onClick={() => setShowScheduleModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreateSchedule} className="p-6 space-y-3">
                {scheduleItems.map((item, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-gray-500">일정 {idx + 1}</span>
                      {scheduleItems.length > 1 && (
                        <button type="button" onClick={() => removeScheduleItem(idx)} className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">카테고리 *</label>
                        <select value={item.scheduleCategory} onChange={(e) => handleScheduleItemChange(idx, 'scheduleCategory', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {SCHEDULE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">제목 *</label>
                        <input type="text" value={item.title} onChange={(e) => handleScheduleItemChange(idx, 'title', e.target.value)}
                          placeholder={`${getCustomerDisplayName(customer)} ${SCHEDULE_CATEGORIES.find(c => c.value === item.scheduleCategory)?.label || ''}`}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">시작일시 *</label>
                        <input type="datetime-local" value={item.startDt} onChange={(e) => handleScheduleItemChange(idx, 'startDt', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">종료일시</label>
                        <input type="datetime-local" value={item.endDt} onChange={(e) => handleScheduleItemChange(idx, 'endDt', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                    <div className="mt-2">
                      <input type="text" value={item.memo} onChange={(e) => handleScheduleItemChange(idx, 'memo', e.target.value)}
                        placeholder="메모" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                ))}
                <button type="button" onClick={addScheduleItem}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors flex items-center justify-center gap-1">
                  <i className="ri-add-line"></i> 일정 추가
                </button>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowScheduleModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-sm">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm">
                    {scheduleItems.length > 1 ? `${scheduleItems.length}건 등록` : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 메모 등록/수정 모달 */}
        {showMemoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">{editingMemo ? '메모 수정' : '메모 등록'}</h2>
                <button onClick={() => setShowMemoModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleSaveMemo} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">상담일시</label>
                    <input type="datetime-local" value={memoForm.consultationDt || ''} onChange={(e) => setMemoForm({ ...memoForm, consultationDt: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">상담유형</label>
                    <select value={memoForm.consultationType || ''} onChange={(e) => setMemoForm({ ...memoForm, consultationType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">선택</option>
                      {CONSULTATION_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예산 범위</label>
                    <input type="text" value={memoForm.budgetRange || ''} onChange={(e) => setMemoForm({ ...memoForm, budgetRange: e.target.value })}
                      placeholder="예: 3000만원 ~ 5000만원"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예상 하객수</label>
                    <input type="text" value={memoForm.expectedGuests || ''} onChange={(e) => setMemoForm({ ...memoForm, expectedGuests: e.target.value })}
                      placeholder="예: 200~300명"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">선호 메뉴</label>
                  <input type="text" value={memoForm.preferredMenu || ''} onChange={(e) => setMemoForm({ ...memoForm, preferredMenu: e.target.value })}
                    placeholder="예: 한식, 뷔페, 양식 코스"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">특별 요청</label>
                  <input type="text" value={memoForm.specialRequests || ''} onChange={(e) => setMemoForm({ ...memoForm, specialRequests: e.target.value })}
                    placeholder="예: 야외 예식 가능 여부, 주차 대수, 포토존"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">내용</label>
                  <textarea value={memoForm.content || ''} onChange={(e) => setMemoForm({ ...memoForm, content: e.target.value })} rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowMemoModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">{editingMemo ? '수정' : '등록'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 추가매출 품목 등록/수정 모달 */}
        {showItemModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-bold">{editingItem ? '품목 수정' : '품목 추가'}</h2>
                <button onClick={() => setShowItemModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleSaveItem} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">카테고리 *</label>
                  <select value={itemForm.itemCategory || 'MEAL'} onChange={(e) => setItemForm({ ...itemForm, itemCategory: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {ITEM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상품명 *</label>
                  <input type="text" value={itemForm.productName || ''} onChange={(e) => setItemForm({ ...itemForm, productName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">수량</label>
                    <input type="number" min={1} value={itemForm.quantity || 1} onChange={(e) => setItemForm({ ...itemForm, quantity: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">단가 *</label>
                    <input type="number" value={itemForm.unitPrice || ''} onChange={(e) => setItemForm({ ...itemForm, unitPrice: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">비고</label>
                  <input type="text" value={itemForm.memo || ''} onChange={(e) => setItemForm({ ...itemForm, memo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowItemModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">{editingItem ? '수정' : '등록'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 결제 상세 모달 */}
        {selectedPayment && (() => {
          const p = selectedPayment;
          const paymentType = p.paymentType || p.payment_type || '';
          const paymentMethod = p.paymentMethod || p.payment_method || '';
          const paidDt = p.paidDt || p.paid_dt || '';
          const status = p.status || '';
          const paymentIdx = p.whPaymentIdx || p.wh_payment_idx;
          const getTypeLabel = (t: string) => ({ DEPOSIT: '예약금', CONTRACT: '계약금', INTERMEDIATE: '중도금', BALANCE: '잔금', ETC: '기타' }[t] || t);
          const getMethodLabel = (m: string) => ({ CARD: '카드', TRANSFER: '계좌이체', CASH: '현금' }[m] || m || '-');
          const getStatusLabel = (s: string) => ({ COMPLETED: '완료', SCHEDULED: '예정', REFUNDED: '환불', CANCELLED: '취소' }[s] || s);
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-bold">결제 상세</h2>
                  <button onClick={() => setSelectedPayment(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><div className="text-xs text-gray-500 mb-1">결제일</div><div className="text-sm font-medium">{formatDate(paidDt)}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제유형</div><div><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_TYPE_COLORS[paymentType] || 'bg-gray-100'}`}>{getTypeLabel(paymentType)}</span></div></div>
                    <div><div className="text-xs text-gray-500 mb-1">금액</div><div className="text-lg font-bold text-gray-900">{Number(p.amount || 0).toLocaleString()}원</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">결제수단</div><div className="text-sm">{getMethodLabel(paymentMethod)}</div></div>
                    <div><div className="text-xs text-gray-500 mb-1">상태</div><div><span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[status] || 'bg-gray-100'}`}>{getStatusLabel(status)}</span></div></div>
                  </div>
                  {p.memo && <div><div className="text-xs text-gray-500 mb-1">메모</div><div className="text-sm text-gray-700">{p.memo}</div></div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                  <button onClick={async () => {
                    if (!confirm('이 결제를 삭제하시겠습니까?')) return;
                    try {
                      await paymentApi.delete(paymentIdx);
                      setSelectedPayment(null);
                      fetchData();
                    } catch (err: any) { alert(`삭제 실패: ${err.message}`); }
                  }} className="text-red-500 hover:text-red-700 text-sm cursor-pointer flex items-center gap-1">
                    <i className="ri-delete-bin-line"></i> 삭제
                  </button>
                  <button onClick={() => setSelectedPayment(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 cursor-pointer">닫기</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* 결제 등록 모달 (여러 건 동시 등록) */}
        {showPaymentModal && (() => {
          const paymentItems = paymentForm.items || [{ paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'CONTRACT', status: 'COMPLETED', paymentMethod: 'CARD', memo: '' }];
          const updatePaymentItem = (idx: number, field: string, value: any) => {
            const newItems = [...paymentItems];
            newItems[idx] = { ...newItems[idx], [field]: value };
            setPaymentForm({ ...paymentForm, items: newItems });
          };
          const addPaymentItem = () => {
            setPaymentForm({ ...paymentForm, items: [...paymentItems, { paidDt: new Date().toISOString().split('T')[0], amount: 0, paymentType: 'INTERMEDIATE', status: 'SCHEDULED', paymentMethod: 'CARD', memo: '' }] });
          };
          const removePaymentItem = (idx: number) => {
            if (paymentItems.length <= 1) return;
            setPaymentForm({ ...paymentForm, items: paymentItems.filter((_: any, i: number) => i !== idx) });
          };

          return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-lg font-bold">결제 등록</h2>
                  <p className="text-xs text-gray-400 mt-0.5">여러 결제를 한번에 등록할 수 있습니다</p>
                </div>
                <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-2xl"></i></button>
              </div>
              <form onSubmit={handleCreatePayment} className="p-6 space-y-3">
                {paymentItems.map((pi: any, idx: number) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-xs font-semibold text-gray-500">결제 {idx + 1}</span>
                      {paymentItems.length > 1 && (
                        <button type="button" onClick={() => removePaymentItem(idx)} className="text-red-500 hover:text-red-700 text-xs cursor-pointer">삭제</button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제일 *</label>
                        <input type="date" value={pi.paidDt || ''} onChange={(e) => updatePaymentItem(idx, 'paidDt', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">금액 *</label>
                        <input type="number" value={pi.amount || ''} onChange={(e) => updatePaymentItem(idx, 'amount', Number(e.target.value))}
                          placeholder="0" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제유형</label>
                        <select value={pi.paymentType || 'CONTRACT'} onChange={(e) => updatePaymentItem(idx, 'paymentType', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">상태</label>
                        <select value={pi.status || 'COMPLETED'} onChange={(e) => updatePaymentItem(idx, 'status', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">결제수단</label>
                        <select value={pi.paymentMethod || 'CARD'} onChange={(e) => updatePaymentItem(idx, 'paymentMethod', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">메모</label>
                        <input type="text" value={pi.memo || ''} onChange={(e) => updatePaymentItem(idx, 'memo', e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  </div>
                ))}

                <button type="button" onClick={addPaymentItem}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors flex items-center justify-center gap-1">
                  <i className="ri-add-line"></i> 결제 추가
                </button>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button type="button" onClick={() => setShowPaymentModal(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">등록</button>
                </div>
              </form>
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
}
